/*
 * PDP upsell A/B test — picks which upsell variant a product page keeps.
 *
 * The product templates in the test carry four upsell blocks, each tagged with
 * data-upsell-test="a|b|c|d" (the block's "PDP upsell test variant" setting).
 * ABConvert's script for each test group marks the page with that group's letter:
 *
 *   <html data-pdp-upsell="c">
 *
 * An inline style rule in main-product.liquid already shows only the matching block
 * the moment that attribute appears. This element does the part CSS cannot: it
 * REMOVES the other three blocks. Hiding is not enough — every kit adds its ticked
 * add-ons on the form's formdata event whether it is visible or not, so a hidden
 * kit could still slip an add-on into the cart.
 *
 * If no valid letter has arrived by `data-timeout` ms after page start, the page
 * falls back to `data-fallback`. Whichever comes first wins and is locked for this
 * page view: a letter arriving later is ignored rather than swapping blocks under
 * the customer.
 *
 * <html data-pdp-upsell-resolved="assigned|fallback|test-off|qa-override"> records
 * which happened, for QA and for measuring how often ABConvert misses the timeout.
 * test-off: the "PDP upsell test running" theme setting is off, so the fallback is
 * used for everyone without waiting.
 * qa-override: ?pdp_upsell=c in the URL, honoured on unpublished themes only.
 *
 * Does nothing in the theme editor, where every block stays visible and editable.
 */
if (!customElements.get('pdp-upsell-test')) {
  const VARIANTS = ['a', 'b', 'c', 'd'];
  const ATTRIBUTE = 'data-pdp-upsell';

  customElements.define(
    'pdp-upsell-test',
    class PdpUpsellTest extends HTMLElement {
      connectedCallback() {
        if (window.Shopify?.designMode || this.resolved) return;

        this.root = document.documentElement;
        this.fallback = VARIANTS.includes(this.dataset.fallback) ? this.dataset.fallback : 'b';

        const override = this.readQaOverride();
        if (override) {
          this.resolve(override, 'qa-override');
          return;
        }

        /* Test switched off in theme settings: the fallback for everyone, whatever a
           leftover ABConvert script may have set. */
        if (this.dataset.testRunning === 'false') {
          this.resolve(this.fallback, 'test-off');
          return;
        }

        const assigned = this.readAssigned();
        if (assigned) {
          this.resolve(assigned, 'assigned');
          return;
        }

        this.observer = new MutationObserver(() => {
          const letter = this.readAssigned();
          if (letter) this.resolve(letter, 'assigned');
        });
        this.observer.observe(this.root, { attributes: true, attributeFilter: [ATTRIBUTE] });

        /* Counted from page start, not from when this deferred script ran. */
        const timeout = parseInt(this.dataset.timeout, 10) || 1500;
        this.timer = setTimeout(
          () => this.resolve(this.fallback, 'fallback'),
          Math.max(0, timeout - performance.now())
        );
      }

      disconnectedCallback() {
        this.stopWaiting();
      }

      /*
        QA: ?pdp_upsell=a|b|c|d forces a variant, ahead of the theme switch and
        ABConvert. Only on an unpublished theme - on the published one a visitor
        could otherwise pick their own variant. Fails closed: if the theme's role
        is unknown, there is no override.
      */
      readQaOverride() {
        const role = window.Shopify?.theme?.role;
        if (!role || role === 'main') return null;

        const letter = (new URLSearchParams(window.location.search).get('pdp_upsell') || '').trim().toLowerCase();
        return VARIANTS.includes(letter) ? letter : null;
      }

      /* The letter on <html>, or null when it is missing or not one we know. */
      readAssigned() {
        const letter = (this.root.getAttribute(ATTRIBUTE) || '').trim().toLowerCase();
        return VARIANTS.includes(letter) ? letter : null;
      }

      resolve(letter, how) {
        if (this.resolved) return;
        this.resolved = true;
        this.stopWaiting();

        this.root.setAttribute(ATTRIBUTE, letter);
        this.root.setAttribute(`${ATTRIBUTE}-resolved`, how);

        /*
          Scoped to the product this element belongs to - the product section, or
          the quick view modal it was copied into - so any other product on the page
          is left alone. The surviving block loses its tag: from here on the inline
          style rule no longer applies to it, so a letter set later cannot hide it.
        */
        const scope =
          this.closest('[id^="MainProduct-"], [id^="QuickAddInfo-"]') || this.closest('.shopify-section') || document;
        scope.querySelectorAll('[data-upsell-test]').forEach((block) => {
          if (block.dataset.upsellTest === letter) {
            block.removeAttribute('data-upsell-test');
          } else {
            block.remove();
          }
        });
      }

      stopWaiting() {
        this.observer?.disconnect();
        this.observer = null;
        clearTimeout(this.timer);
      }
    }
  );
}
